import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './SelectBoxWithSearch.css'
import { baseurl } from '../../../api';

const SearchSelectBoxDrown = ({ 
  options = [], 
  onChange, 
  defaultValue = '', 
  label = '', 
  className = '', 
  border = '' ,
  field = {},
  error = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);

  // Memoize options map for faster lookups
  const optionsMap = useMemo(() => {
    const map = new Map();
    options.forEach(option => {
      if (option && typeof option.label === 'string') {
        map.set(option.value, option);
      }
    });
    
    // Debug logging
    console.log('Options map created:', {
      totalOptions: options.length,
      validOptions: map.size,
      sampleKeys: Array.from(map.keys()).slice(0, 3),
      keyTypes: Array.from(map.keys()).slice(0, 3).map(k => typeof k)
    });
    
    return map;
  }, [options]);

  useEffect(() =>{
    console.log("fields",field)
  },[field])

  // Initialize selected value based on defaultValue
  useEffect(() => {
    console.log('DefaultValue effect triggered:', {
      defaultValue,
      type: typeof defaultValue,
      hasInMap: optionsMap.has(defaultValue),
      hasAsString: optionsMap.has(String(defaultValue)),
      hasAsNumber: optionsMap.has(Number(defaultValue))
    });
    
    if (defaultValue !== null && defaultValue !== undefined && defaultValue !== '') {
      // Handle both string and number comparisons
      const matchedOption = optionsMap.get(defaultValue) || 
                           optionsMap.get(String(defaultValue)) || 
                           optionsMap.get(Number(defaultValue));
      
      if (matchedOption) {
        console.log('Found matched option:', matchedOption);
        setSelected(matchedOption);
      } else {
        console.warn(`No option found for defaultValue: ${defaultValue}`, 'Available options:', Array.from(optionsMap.keys()));
        setSelected(null);
      }
    } else {
      console.log('DefaultValue is empty, setting selected to null');
      setSelected(null);
    }
  }, [defaultValue, optionsMap]);

  // Memoized filtered options with debouncing effect
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return Array.from(optionsMap.values());
    }
    
    const searchLower = searchTerm.toLowerCase();
    return Array.from(optionsMap.values()).filter(option => 
      option.label.toLowerCase().includes(searchLower) // Changed from startsWith to includes for better UX
    );
  }, [searchTerm, optionsMap]);

  // Memoized select handler to prevent unnecessary re-renders
  const handleSelect = useCallback((option) => {
    setSelected(option);
    setIsOpen(false);
    setSearchTerm('');
    
    if (onChange) {
      onChange({
        target: {
          value: option.value
        }
      });
    }
  }, [onChange]);

  // Memoized search handler
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Memoized toggle handler
  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Reset search when closing
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard navigation (bonus optimization)
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  return (
    <div 
      className={`search-select-dropdown-container ${className}`}  
      ref={dropdownRef}
    >
      <div
        className={`search-select-dropdown-header ${border} ${error ? 'input-error' : ''}`}
        onClick={handleToggle}
      >
        <div className="floating-label-wrapper">
          <label className={`search-select-label fts-15 common-text color-999 ftw-4 ${selected ? 'has-value' : ''}`}>
            {label}
          </label>
          <span className="selected-value fts-15 ftw-4 common-text">
            {selected?.label || ''}
          </span>
        </div>
        <svg 
          className={`search-select-dropdown-arrow ${isOpen ? 'search-select-open' : ''}`} 
          width="8" 
          height="6" 
          viewBox="0 0 8 6" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 6L0.535899 0L7.4641 0L4 6Z" fill="#424242"/>
        </svg>
      </div>
      
      {isOpen && (
        <div className="search-select-dropdown-panel">
          <div className="search-select-search-container">
            <svg 
              className="search-select-search-icon" 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M11.8889 11.8889L15 15M1 7.22222C1 8.87246 1.65555 10.4551 2.82245 11.622C3.98934 12.7889 5.57199 13.4444 7.22222 13.4444C8.87246 13.4444 10.4551 12.7889 11.622 11.622C12.7889 10.4551 13.4444 8.87246 13.4444 7.22222C13.4444 5.57199 12.7889 3.98934 11.622 2.82245C10.4551 1.65555 8.87246 1 7.22222 1C5.57199 1 3.98934 1.65555 2.82245 2.82245C1.65555 3.98934 1 5.57199 1 7.22222Z" 
                stroke="#424242" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="search-select-search-input fts-12 ftw-4 common-text"
              placeholder="Search..."
              autoFocus
            />
          </div>
          
          <div className="search-select-options-container">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="search-select-option-item fts-15 ftw-4 dk-gray common-text d-flex ai-center"style={{ gap: '6px' }}
                  onClick={() => handleSelect(option)}
                >
                  {field?.showFlag && option.country_flag && (
                    <img
                      src={`${baseurl.replace(/\/$/, '')}${option.country_flag}`}
                      alt={`${option.label} flag`}
                      className='country-dropdown-image'
                    />
                  )}
                  {option.label}
                </div>
              ))
            ) : (
              <div className="search-select-no-results fts-15 ftw-4 color-999 common-text">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
      {error && (
        <div className="input-error-message fts-12">
          {error}
        </div>
      )}
    </div>
  );
};

export default SearchSelectBoxDrown;